import { ArrowRight, Check, Clock, Lock } from "lucide-react"

import { Button } from "@/components/ui/button"
import { CountdownText, useCountdown } from "./Countdown"
import { Eyebrow, Panel } from "./Panel"
import PriceCard from "./PriceCard"
import { formatDollars, savingsPct } from "./format"

const INCLUDED = [
  { text: "Subscriber-only offers and bundles" },
  { text: "Early access to promos" },
  { text: "Insider-only guides and templates", soon: true },
]

// How much less writing costs on Insider across the book tiers, from the live
// rows: null when there is no price data, { min: 0, max: 0 } when the lists
// match, otherwise the lowest and highest tier saving in percent.
export function writingSavings(rows) {
  const priced = (rows ?? []).filter((r) => r.standard_cents != null && r.insider_cents != null)
  if (!priced.length) return null
  const pcts = priced.map((r) => savingsPct(r.standard_cents, r.insider_cents) ?? 0)
  return { min: Math.min(...pcts), max: Math.max(...pcts) }
}

const rangeText = ({ min, max }) => (min === max ? `about ${max}%` : `${min}% to ${max}%`)

// The before/after for the first book row (Premium), from the real price
// lists. Hidden when that row saves nothing today.
function BeforeAfter({ row, savings }) {
  return (
    <div className="mt-[28px] flex flex-col gap-[6px]">
      <div className="text-[12.5px] font-semibold text-ink-3">
        Your next {row.word_count.toLocaleString("en-US")}-word {row.label} book
      </div>
      <div className="flex flex-wrap items-baseline gap-[14px] tabular-nums">
        <span className="font-display text-[26px] font-semibold tracking-[-0.5px] text-ink-3 line-through decoration-2 max-[561px]:text-[21px]">
          {formatDollars(row.standard_cents)}
        </span>
        <ArrowRight className="size-5 self-center text-ink-3" strokeWidth={2} aria-hidden />
        <span className="font-display text-[48px] leading-none font-bold tracking-[-1.6px] text-foreground max-[561px]:text-[38px]">
          {formatDollars(row.insider_cents)}
        </span>
        <span className="self-center rounded-full bg-done/15 px-[11px] py-[4px] text-[13px] font-bold text-done">
          Save {formatDollars(row.standard_cents - row.insider_cents)}
        </span>
      </div>
      <div className="mt-[4px] text-[13px] text-muted-foreground">
        {savings.min === savings.max
          ? `About ${savings.max}% off writing on every tier.`
          : `${savings.min}% to ${savings.max}% off writing, depending on the tier.`}{" "}
        Applied automatically, no codes.
      </div>
    </div>
  )
}

export default function OfferHero({ plan, comparison, endpoints, closesAt, heroRef }) {
  const left = useCountdown(closesAt)
  const row = comparison?.bookRows?.[0]
  const pct = row ? savingsPct(row.standard_cents, row.insider_cents) : null
  const savings = writingSavings(comparison?.bookRows)

  return (
    <Panel
      ref={heroRef}
      className="grid grid-cols-[minmax(0,1.7fr)_minmax(260px,1fr)] overflow-hidden shadow-[0_24px_60px_-34px_rgba(0,0,0,0.28)] max-[1100px]:grid-cols-1"
    >
      <div className="px-[40px] pt-[40px] pb-[34px] max-[861px]:px-[22px] max-[861px]:py-[26px] max-[561px]:px-[18px] max-[561px]:py-[20px]">
        <Eyebrow>
          <Lock strokeWidth={2.2} /> Insider membership
        </Eyebrow>
        <h1 className="my-[14px] max-w-[600px] font-display text-[40px] leading-[1.08] font-bold tracking-[-1.3px] text-balance max-[861px]:text-[30px] max-[861px]:tracking-[-0.8px] max-[561px]:text-[26px]">
          Pay the previous price list on every order
        </h1>
        <p className="max-w-[560px] text-[15px] leading-[1.6] text-pretty text-muted-foreground">
          Insider members are billed at last generation&apos;s rates.
          {savings?.max > 0 && ` Today that's ${rangeText(savings)} less on writing.`}
          {savings?.max === 0 && " Today both price lists match, so your savings start with the next increase."}{" "}
          When standard prices go up, you move to the old standard price, so you stay one step behind every
          increase.
        </p>
        {pct && row.word_count ? <BeforeAfter row={row} savings={savings} /> : null}
        <div className="mt-[22px] flex flex-wrap gap-[10px]">
          <Button asChild variant="outline" className="h-auto px-[15px] py-[9px] text-[13px] font-semibold">
            <a href="#calc">
              Is it worth it for me? <ArrowRight strokeWidth={2.2} />
            </a>
          </Button>
          <Button asChild variant="ghost" className="h-auto px-[15px] py-[9px] text-[13px] font-semibold text-muted-foreground">
            <a href="#compare">See all prices</a>
          </Button>
        </div>
      </div>

      <PriceCard
        plan={plan}
        endpoints={endpoints}
        fine="Billed monthly through Stripe. Cancel anytime, but you can't rejoin afterwards."
        className="m-[14px] self-start px-[26px] py-[28px] max-[1100px]:mt-0"
      >
        <div className="flex flex-col gap-[8px] border-t border-border pt-[14px]">
          <div className="text-[10.5px] font-bold tracking-[0.08em] text-ink-3 uppercase">Also included</div>
          {INCLUDED.map((item) => (
            <div key={item.text} className="flex items-start gap-[8px] text-[12.5px] leading-[1.45] text-muted-foreground">
              <Check className="mt-[2px] size-[14px] shrink-0 text-foreground" strokeWidth={2.4} />
              <span>
                {item.text}
                {item.soon && <span className="ml-[4px] text-[11px] text-ink-3">· coming soon</span>}
              </span>
            </div>
          ))}
        </div>
        {left && (
          <div className="flex flex-wrap items-center gap-[8px] border-t border-border pt-[14px] text-[12.5px] font-semibold text-muted-foreground">
            <Clock className="size-[15px] shrink-0 text-ink-3" strokeWidth={2} />
            Enrollment closes in <CountdownText left={left} />
          </div>
        )}
      </PriceCard>
    </Panel>
  )
}
